/**
 * CORE-872 — форматирование карточек программы. Вынесено из ProgramsTab, чтобы
 * правила витрины проверялись без рендера: пустая цена и бессрочный доступ —
 * это разные состояния, и путать их нельзя ни на карточке, ни в тесте.
 */

const CURRENCY_SYMBOLS: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

/**
 * «4 900 ₽» / «Бесплатно» / «Цена не указана».
 *
 * Незаполненная цена — обычное состояние черновика, и показать её как «0 ₽»
 * значит соврать: у мастера это не бесплатный продукт, а недописанный.
 * Незнакомый код валюты печатается как есть, а не проглатывается.
 */
export function formatProgramPrice(price: number | null, currency: string | null): string {
  if (price == null) return 'Цена не указана';
  if (price === 0) return 'Бесплатно';
  const amount = new Intl.NumberFormat('ru-RU').format(price);
  const symbol = currency ? (CURRENCY_SYMBOLS[currency] ?? currency) : '';
  return symbol ? `${amount} ${symbol}` : amount;
}

/**
 * «Доступ бессрочный» / «Доступ 6 месяцев» / «Доступ 45 дн.».
 *
 * `null` в сроке — это и есть «бессрочный» (так его хранит бэк), поэтому оба
 * признака ведут к одному ответу, а не к пустой строке.
 */
export function formatProgramAccess(days: number | null, unlimited: boolean): string {
  if (unlimited || days == null) return 'Доступ бессрочный';
  if (days % 30 === 0) {
    const months = days / 30;
    const word = months === 1 ? 'месяц' : months < 5 ? 'месяца' : 'месяцев';
    return `Доступ ${months} ${word}`;
  }
  return `Доступ ${days} дн.`;
}

/** «Изменено 20 августа» для карточки черновика. Битую дату не печатаем вовсе. */
export function formatProgramUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/**
 * CORE-999 — «Шаг 3 из 5» на карточке черновика (Макет 1).
 *
 * Считается от ПОСЛЕДНЕГО ЗАВЕРШЁННОГО шага, а не от того, который мастер
 * последним видел: обещание карточки и то, куда уводит «Продолжить», должны
 * совпадать, иначе счётчик показывает одно, а визард открывает другое. Правило
 * перехода живёт в `resumeStep` (programSteps.ts) и здесь повторено ровно в той
 * же форме.
 *
 * Ничего не завершив, мастер стоит на первом шаге — «Шаг 1 из 5», а не «0».
 * Дошедший до конца остаётся на пятом: дальше шагов нет.
 */
export function formatProgramStep(lastCompletedStep: number | null): string {
  const done = lastCompletedStep ?? 0;
  const step = done <= 0 ? 1 : done >= PROGRAM_STEP_COUNT ? PROGRAM_STEP_COUNT : done + 1;
  return `Шаг ${step} из ${PROGRAM_STEP_COUNT}`;
}

/** Шагов у обоих продуктов ровно пять — см. programSteps.ts / CORE-993. */
export const PROGRAM_STEP_COUNT = 5;

/** Русская форма по числу: 1 раздел / 2 раздела / 5 разделов. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/**
 * CORE-872 — краткий состав программы на публичной карточке (Макет 8):
 * «2 раздела · 4 материала · 21 минута».
 *
 * Отдельная строка от срока доступа, а не вместо него: покупателю нужно и то, и
 * другое (ТЗ §16, уточнение QA 04.09.2026). Нулевые части опускаются — у
 * единичной «платной практики» разделов нет вовсе, и «0 разделов» на её карточке
 * было бы шумом; пустой состав целиком не печатается.
 *
 * Данные приходят в списочной выдаче `PublicProgramCard.outline`. Поле
 * необязательное: на старом бэкенде, который счётчики состава в список ещё не
 * кладёт (см. бэкендовый сабтаск CORE-872), карточка просто не показывает эту
 * строку, а не рисует «0 материалов».
 */
export function formatProgramOutline(
  outline: { sectionCount: number; materialCount: number; totalDurationMinutes: number } | null | undefined,
): string {
  if (!outline) return '';
  const parts: string[] = [];
  if (outline.sectionCount > 0) {
    parts.push(`${outline.sectionCount} ${plural(outline.sectionCount, 'раздел', 'раздела', 'разделов')}`);
  }
  if (outline.materialCount > 0) {
    parts.push(`${outline.materialCount} ${plural(outline.materialCount, 'материал', 'материала', 'материалов')}`);
  }
  if (outline.totalDurationMinutes > 0) {
    parts.push(`${outline.totalDurationMinutes} ${plural(outline.totalDurationMinutes, 'минута', 'минуты', 'минут')}`);
  }
  return parts.join(' · ');
}

/**
 * CORE-993 — «Программа» / «Платная практика».
 *
 * Ярлык нужен везде, где два продукта лежат в одном списке: в «Созданном» они
 * отличаются только им, и мастер, у которого черновики называются «Дыхание» и
 * «Дыхание 2», иначе не поймёт, какой из них курс, а какой — одна практика.
 */
export function formatProductType(productType: 'program' | 'paidPractice'): string {
  return productType === 'paidPractice' ? 'Платная практика' : 'Программа';
}
